import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { getUserCredential } from "src/lib/cookies";

import { getHoursWorkedTrend, getCalendarPeriods } from "../services";
import { TREND_DATE_RANGE } from "../utils/dateRangeHelper";

/*
 * Data for the Hours Worked Trend card (graph2).
 *
 * Only the granularity strip is a request parameter (in_group_by). Every row
 * comes back with hours and amount both populated, so the Hours / Amount strip
 * just picks a field off data already in hand - no refetch, no loading flash.
 */

// A stable default: scope is an effect dependency, so a fresh {} on every render would refetch forever.
const NO_SCOPE = {};

export const useHoursWorkedTrend = (
  providerId,
  periodTypeId = null,
  selectedPeriodName = null,
  periodOptions = [],
  scope = NO_SCOPE
) => {
  const dispatch = useDispatch();
  const cookiesData = useMemo(() => getUserCredential(), []);

  const [metric, setMetric] = useState("hours");
  const [granularity, setGranularity] = useState(periodOptions.length > 0 ? periodOptions[0].period_type_mstr_key : 1);
  const [rows, setRows] = useState([]);
  const [calendarPeriods, setCalendarPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const requestRef = useRef(0);

  useEffect(() => {
    if (!providerId) return undefined;

    const token = requestRef.current + 1;
    requestRef.current = token;
    setIsLoading(true);

    (async () => {
      const [data, periods] = await Promise.all([
        getHoursWorkedTrend(
          cookiesData,
          dispatch,
          {
            providerId,
            periodTypeMasterKey: granularity,
            startDate: TREND_DATE_RANGE.startDate,
            endDate: TREND_DATE_RANGE.endDate,
          },
          scope
        ),
        getCalendarPeriods(cookiesData, dispatch, granularity),
      ]);

      if (requestRef.current !== token) return;

      setRows(data);
      setCalendarPeriods(periods);
      setIsLoading(false);
    })();

    return () => {
      requestRef.current += 1;
    };
  }, [cookiesData, dispatch, providerId, granularity, scope]);


  const isAmount = metric === "amount";

  const chartData = useMemo(
    () =>
      rows.map((row) => ({
        category: row.category,
        value: isAmount ? row.amount : row.hours,
      })),
    [rows, isAmount],
  );

  /* "1708h 00m logged" / "$1,708.00 logged" under the title. */
  const total = useMemo(
    () => chartData.reduce((sum, point) => sum + (Number(point.value) || 0), 0),
    [chartData],
  );


  return {
    metric,
    setMetric: useCallback((value) => setMetric(value), []),
    granularity,
    setGranularity: useCallback((value) => setGranularity(value), []),
    chartData,
    calendarPeriods,
    total,
    isAmount,
    isLoading,
  };
};

export default useHoursWorkedTrend;
